import type { TaskCardProps } from "@features/dashboard";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box, Button, ScrollButtonFlatlist, useDevice } from "@memorang/ui";
import { Image } from "expo-image";
import { Card, ProgressBar, Text } from "react-native-paper";
import type { EXAM_STATUS } from "types/tag";

type ModuleRecommendationItem = TaskCardProps & {
	numCompleted?: number;
	numItems?: number;
};

type ModuleRecommendationContentProps = {
	item: ModuleRecommendationItem;
	variant: "inline" | "card";
};

const getRecommendationMetadata = (subtitle?: string) => {
	const [typeLabel, durationLabel] = subtitle?.split(/\s*[•·]\s*/) ?? [];

	return {
		typeLabel,
		durationLabel,
	};
};

const getRecommendationCta = (status?: EXAM_STATUS) => {
	if (status === "ACTIVE") {
		return {
			label: "Resume",
			icon: "play-pause" as const,
			mode: "outlined" as const,
		};
	}
	if (status === "COMPLETE") {
		return {
			label: "View results",
			icon: "file-chart-outline" as const,
			mode: "text" as const,
		};
	}
	return {
		label: "Start",
		icon: "play-circle-outline" as const,
		mode: "contained" as const,
	};
};

export const ModuleRecommendationContent = ({
	item,
	variant,
}: ModuleRecommendationContentProps) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	const cta = getRecommendationCta(item.status);
	const { typeLabel, durationLabel } = getRecommendationMetadata(item.subtitle);
	const progress =
		item.numItems && item.numItems > 0
			? Math.min((item.numCompleted ?? 0) / item.numItems, 1)
			: 0;
	const thumbnailSize = variant === "inline" ? 56 : 44;
	const subtitle = variant === "card" ? typeLabel : item.subtitle;

	if (variant === "card") {
		return (
			<Box flex={1} gap={14}>
				<Box flexDirection="row" gap={12} alignItems="flex-start">
					{item.thumbnailSource ? (
						<Image
							source={item.thumbnailSource}
							style={{
								width: thumbnailSize,
								height: thumbnailSize,
								borderRadius: theme.roundness,
							}}
							contentFit="contain"
							alt={item.title}
						/>
					) : null}
					<Box flex={1} gap={2} minWidth={0}>
						<Text
							variant="titleSmall"
							style={{
								fontWeight: "700",
								color: theme.colors.onSurface,
								lineHeight: 20,
							}}
							numberOfLines={2}
						>
							{item.title}
						</Text>
						{subtitle ? (
							<Text
								variant="bodySmall"
								style={{ color: theme.colors.onSurfaceVariant }}
								numberOfLines={1}
							>
								{subtitle}
							</Text>
						) : null}
					</Box>
				</Box>
				<ProgressBar
					progress={progress}
					color={theme.colors.primary}
					style={{
						height: 8,
						backgroundColor: theme.colors.surfaceVariant,
					}}
				/>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
				>
					{durationLabel ? (
						<Text
							variant="bodySmall"
							style={{ color: theme.colors.onSurfaceVariant }}
							numberOfLines={1}
						>
							{durationLabel}
						</Text>
					) : (
						<Box />
					)}
					<Button
						mode={cta.mode}
						icon={cta.icon}
						onPress={item.handleClickTask}
						loading={item.loading}
						disabled={item.loading}
						style={{ borderRadius: 999 }}
						contentStyle={{ flexDirection: "row-reverse" }}
					>
						{cta.label}
					</Button>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			flexDirection={variant === "inline" || isMobile ? "row" : "column"}
			alignItems={variant === "inline" ? "center" : "flex-start"}
			justifyContent="space-between"
			gap={variant === "inline" ? 16 : 12}
			flex={1}
		>
			{item.thumbnailSource ? (
				<Image
					source={item.thumbnailSource}
					style={{
						width: thumbnailSize,
						height: thumbnailSize,
						borderRadius: theme.roundness,
					}}
					contentFit="contain"
					alt={item.title}
				/>
			) : null}
			<Box flex={1} gap={4} minWidth={0}>
				<Text
					variant="titleMedium"
					style={{ fontWeight: "bold", color: theme.colors.onSurface }}
					numberOfLines={2}
				>
					{item.title}
				</Text>
				{subtitle ? (
					<Text
						variant="bodyMedium"
						style={{ color: theme.colors.onSurfaceVariant }}
						numberOfLines={1}
					>
						{subtitle}
					</Text>
				) : null}
			</Box>
			<Button
				mode={cta.mode}
				icon={cta.icon}
				onPress={item.handleClickTask}
				loading={item.loading}
				disabled={item.loading}
				style={{ borderRadius: 999 }}
				contentStyle={{ flexDirection: "row-reverse" }}
			>
				{cta.label}
			</Button>
		</Box>
	);
};

type ModuleRecommendationsSectionProps = {
	items: ModuleRecommendationItem[];
	loading?: boolean;
};

export const ModuleRecommendationsSection = ({
	items,
}: ModuleRecommendationsSectionProps) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();

	return (
		<ScrollButtonFlatlist
			data={items}
			horizontal
			showsHorizontalScrollIndicator={false}
			keyExtractor={(item) => `${item.title}-${item.status}`}
			contentContainerStyle={{
				paddingHorizontal: isMobile ? 16 : 2,
				gap: 16,
				paddingBottom: 8,
			}}
			renderItem={({ item }) => (
				<Card
					mode="outlined"
					style={{
						width: isMobile ? 320 : 360,
						borderRadius: theme.roundness,
						backgroundColor: theme.colors.surface,
						borderColor: theme.colors.outlineVariant,
					}}
				>
					<Card.Content style={{ padding: 16 }}>
						<ModuleRecommendationContent item={item} variant="card" />
					</Card.Content>
				</Card>
			)}
		/>
	);
};
